// src/pages/courier/CourierFormModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Building2, Globe, Phone, Mail, User, FileText } from 'lucide-react';

const CourierFormModal = ({ isOpen, onClose, onSave, editingCourier = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    website: '',
    contactPerson: '',
    contactNumber: '',
    email: '',
    registrationNumber: '',
    gstNumber: '',
    pan: '',
    otherDetails: '',
    active: true
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingCourier) {
      setFormData({
        name: editingCourier.name || '',
        logo: editingCourier.logo || '',
        website: editingCourier.website || '',
        contactPerson: editingCourier.contactPerson || '',
        contactNumber: editingCourier.contactNumber || '',
        email: editingCourier.email || '',
        registrationNumber: editingCourier.business?.registrationNumber || '',
        gstNumber: editingCourier.business?.gstNumber || '',
        pan: editingCourier.business?.pan || '',
        otherDetails: editingCourier.business?.otherDetails || '',
        active: editingCourier.active !== undefined ? editingCourier.active : true
      });
    } else {
      setFormData({
        name: '',
        logo: '',
        website: '',
        contactPerson: '',
        contactNumber: '',
        email: '',
        registrationNumber: '',
        gstNumber: '',
        pan: '',
        otherDetails: '',
        active: true
      });
    }
    setErrors({});
  }, [editingCourier, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData((prev) => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Courier company name is required';
    if (!formData.contactPerson.trim()) errs.contactPerson = 'Contact person is required';
    if (!formData.contactNumber.trim()) errs.contactNumber = 'Contact phone number is required';
    if (!formData.email.trim()) errs.email = 'Email address is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        logo: formData.logo,
        website: formData.website.trim(),
        contactPerson: formData.contactPerson.trim(),
        contactNumber: formData.contactNumber.trim(),
        email: formData.email.trim(),
        business: {
          registrationNumber: formData.registrationNumber.trim(),
          gstNumber: formData.gstNumber.trim().toUpperCase(),
          pan: formData.pan.trim().toUpperCase(),
          otherDetails: formData.otherDetails.trim()
        },
        active: formData.active
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      console.error('Save courier error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cm-modal-overlay">
      <div className="cm-modal-container cm-modal-lg">
        <div className="cm-modal-header">
          <div className="cm-modal-title-group">
            <div className="cm-modal-icon-badge">
              <Building2 size={22} color="#8a6b32" />
            </div>
            <div>
              <h3 className="cm-modal-title">
                {editingCourier ? 'Edit Courier Company' : 'Add Courier Company'}
              </h3>
              <p className="cm-modal-subtitle">
                SuperAdmin master-level partner onboarding (No self-service)
              </p>
            </div>
          </div>
          <button className="cm-btn-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="cm-modal-body">
          {/* Section 1: Basic Info */}
          <div className="cm-form-section">
            <h4 className="cm-form-section-title">
              <span>1</span> Basic Information
            </h4>
            <div className="cm-form-grid">
              <div className="cm-form-group cm-col-full">
                <label className="cm-label">
                  Courier Company Name <span className="cm-req">*</span>
                </label>
                <div className="cm-input-wrapper">
                  <Building2 size={16} className="cm-input-icon" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. BlueDart Express, Delhivery Logistics"
                    className={`cm-input ${errors.name ? 'cm-input-error' : ''}`}
                  />
                </div>
                {errors.name && <span className="cm-error-msg">{errors.name}</span>}
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Website</label>
                <div className="cm-input-wrapper">
                  <Globe size={16} className="cm-input-icon" />
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://www.courierexample.com"
                    className="cm-input"
                  />
                </div>
              </div>

              <div className="cm-form-group">
                <label className="cm-label">Company Logo</label>
                <div className="cm-logo-upload-row">
                  {formData.logo ? (
                    <div className="cm-logo-preview">
                      <img src={formData.logo} alt="Logo" />
                      <button
                        type="button"
                        className="cm-logo-remove-btn"
                        onClick={() => setFormData(prev => ({ ...prev, logo: '' }))}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="cm-logo-placeholder">No Logo</div>
                  )}
                  <label className="cm-btn-secondary cm-btn-sm cm-file-label">
                    Choose Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              <div className="cm-form-group">
                <label className="cm-label">
                  Primary Contact Person <span className="cm-req">*</span>
                </label>
                <div className="cm-input-wrapper">
                  <User size={16} className="cm-input-icon" />
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    placeholder="e.g. Rajesh Sharma (Head of Operations)"
                    className={`cm-input ${errors.contactPerson ? 'cm-input-error' : ''}`}
                  />
                </div>
                {errors.contactPerson && <span className="cm-error-msg">{errors.contactPerson}</span>}
              </div>

              <div className="cm-form-group">
                <label className="cm-label">
                  Contact Phone Number <span className="cm-req">*</span>
                </label>
                <div className="cm-input-wrapper">
                  <Phone size={16} className="cm-input-icon" />
                  <input
                    type="tel"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    placeholder="e.g. +91 98765 43210"
                    className={`cm-input ${errors.contactNumber ? 'cm-input-error' : ''}`}
                  />
                </div>
                {errors.contactNumber && <span className="cm-error-msg">{errors.contactNumber}</span>}
              </div>

              <div className="cm-form-group cm-col-full">
                <label className="cm-label">
                  Official Email Address <span className="cm-req">*</span>
                </label>
                <div className="cm-input-wrapper">
                  <Mail size={16} className="cm-input-icon" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="support@courier.com"
                    className={`cm-input ${errors.email ? 'cm-input-error' : ''}`}
                  />
                </div>
                {errors.email && <span className="cm-error-msg">{errors.email}</span>}
              </div>
            </div>
          </div>

          {/* Section 2: Business & Tax Information */}
          <div className="cm-form-section">
            <h4 className="cm-form-section-title">
              <span>2</span> Business Information
            </h4>
            <div className="cm-form-grid">
              <div className="cm-form-group">
                <label className="cm-label">Business Registration Number (CIN / ROC)</label>
                <div className="cm-input-wrapper">
                  <FileText size={16} className="cm-input-icon" />
                  <input
                    type="text"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleChange}
                    placeholder="e.g. U63090DL2011PTC221234"
                    className="cm-input"
                  />
                </div>
              </div>

              <div className="cm-form-group">
                <label className="cm-label">GST Number (15-digit)</label>
                <div className="cm-input-wrapper">
                  <FileText size={16} className="cm-input-icon" />
                  <input
                    type="text"
                    name="gstNumber"
                    value={formData.gstNumber}
                    onChange={handleChange}
                    placeholder="e.g. 21AABCC1234F1Z5"
                    className="cm-input uppercase"
                    maxLength={15}
                  />
                </div>
              </div>

              <div className="cm-form-group">
                <label className="cm-label">PAN / Business Tax Identifier</label>
                <div className="cm-input-wrapper">
                  <FileText size={16} className="cm-input-icon" />
                  <input
                    type="text"
                    name="pan"
                    value={formData.pan}
                    onChange={handleChange}
                    placeholder="e.g. AABCC1234F"
                    className="cm-input uppercase"
                    maxLength={10}
                  />
                </div>
              </div>

              <div className="cm-form-group cm-col-full">
                <label className="cm-label">Other Registration / Contract Details</label>
                <textarea
                  name="otherDetails"
                  value={formData.otherDetails}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Additional contractual notes or registration credentials"
                  className="cm-textarea"
                />
              </div>

              <div className="cm-form-group cm-col-full">
                <label className="cm-checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                  />
                  <span>Active Courier Partner (Eligible for future order assignments)</span>
                </label>
              </div>
            </div>
          </div>

          <div className="cm-modal-footer">
            <button type="button" className="cm-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="cm-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingCourier ? 'Update Courier' : 'Create Courier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourierFormModal;
